import { FileType } from 'services/file.service';
import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { newClip } from 'components/app/_helpers/_app.helper';
import { Error } from 'components/app/_helpers/app-errors.helper';
import { passFileAccessedAt } from 'components/app/_mediator/app-files.slice';
import { handleSbLinks, sbApi } from 'components/app/editor/_mediator/editor-clips/editor-clips-sb.helper';
import { selectSelectedFile } from 'components/app/_mediator/app-files.slice';
import { Clip, UNLINKED_ID, handleClips, selectStats } from 'components/app/editor/_mediator/editor.slice';
import { setRecentlyMovedClips } from 'components/app/editor/_mediator/editor-controls.slice';
import { useEditorCb } from 'components/app/editor/_mediator/use-editor-cb';
import { AddClipAction } from '../editor-actions.slice';
import { generateLinkId, isLinksValid } from './editor-clips-linking.helper';
import { handleNewClips, clipsApi } from './editor-clips.helper';

export const useClipsAdd = () => {
  const { getPlayerRef } = useEditorCb();
  const { activeClip, storyBoard, clipsMap, linkedClips, selector } = useAppSelector((state) => state.editor);
  const sbPosition = useAppSelector((state) => state.editor__controls.sbPosition);
  const selectedFile = useAppSelector(selectSelectedFile);
  const stats = useAppSelector(selectStats);
  const dispatch = useAppDispatch();

  const createNewClips = (clipTime: number, action: AddClipAction, highlightId?: number) => {
    if (!clipsMap || !selectedFile) return [];
    const clips: Clip[] = [];
    if (action === 'split') {
      clips[0] = newClip({
        mediaFileId: selectedFile.id,
        startTime: null,
        endTime: clipTime,
      });
      clips[1] = newClip({
        mediaFileId: selectedFile.id,
        startTime: clipTime,
        endTime: null,
      });
    } else {
      clips[0] = newClip({
        mediaFileId: selectedFile.id,
        startTime: action === 'startTime' ? clipTime : null,
        endTime: action === 'endTime' ? clipTime : null,
      });
    }
    // Set highlightId if provided (for pinning highlights)
    if (highlightId != null) {
      clips.forEach((clip) => (clip.highlightId = highlightId));
    }
    return handleNewClips(clips, clipTime, clipsMap, linkedClips);
  };

  return (action: AddClipAction = 'startTime', highlightId?: number) => {
    if (!stats || !selectedFile || !selector) return;
    if (selectedFile.fileType !== FileType.video) return;
    if (stats.available === 0) return;

    const player = getPlayerRef();
    if (!player) return;
    const clipTime = player.getCurrentTime();
    const duration = player.getDuration();

    if (clipTime === 0 && action !== 'startTime') {
      Error.addClip.invalidEndTime();
      return;
    } else if (clipTime === duration && action !== 'endTime') {
      Error.addClip.invalidStartTime();
      return;
    }

    const clips = createNewClips(clipTime, action, highlightId);
    if (!clips.length) return;
    if (clips.length > stats.available) {
      Error.tooManyClips(clips.length, stats.available);
      return;
    }

    if (clips.length === 2 && clips[0].clipGroupId === UNLINKED_ID) {
      const linkId = generateLinkId(linkedClips);
      clips.forEach((clip) => (clip.clipGroupId = linkId));
    }

    const res = clipsApi(selector, storyBoard, linkedClips).add(
      clips,
      sbApi(storyBoard).validateAddClipPosition(sbPosition, action)
    );

    if (!isLinksValid(res.linkedClips)) {
      Error.linkingNotPossible();
      return;
    }
    const res1 = handleSbLinks(res.linkedClips, res.storyBoard, storyBoard);
    if (res1.status === 'cantBeFixed') {
      Error.handleSbLinks();
      return;
    }

    dispatch(setRecentlyMovedClips(clips.map((clip) => clip.clipId)));
    dispatch(passFileAccessedAt(selectedFile.id));
    dispatch(
      handleClips({
        data: { ...res, storyBoard: res1.storyBoard },
        backupParams: { title: 'Add Clip', action: 'addClip', activeClip: [activeClip, clips[0].clipId] },
      })
    );
  };
};
